"use client";
import React, { useEffect } from "react";
import VariantsComponents from "./variantsComponents";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { setClientVariants } from "../../../store/slices/pdpSlice";

/**
 * Combo Components for handling client-side variant data and dispatching it to the Redux store.

 * @param {Array<Object>} props.serverVariantProducts - Array of server variant products.
 * @param {Array<Object>} props.isMobile - is mobile check.
 * @returns {React.Component} The Combo Components .
 */
const ComboComponents = ({ serverVariantProducts, isMobile }) => {
  const dispatch = useDispatch();

  const { clientVariants } = useSelector(
    (state) => ({
      clientVariants: state.pdp.clientVariants,
    }),
    shallowEqual,
  );

  /**
   * Checks if an object in the array has a specific label, type, and flavor, and updates the object with new data if found.
   *
   * @param {Array<Object>} modifyVariantProducts - The array to search through.
   * @param {string} variantLabel - The label to look for in each object.
   * @param {string} variantType - The type associated with the label.
   * @param {Object} data - The data to update the object with if the label and type are found.
   * @returns {boolean} - Returns `true` if the object with the specified label and type is found and updated, otherwise `false`.
   */
  const isObjectParentFound = (modifyVariantProducts, variantLabel, variantType, variantFlavor, data) => {
    let isFound = false;

    modifyVariantProducts?.forEach((item) => {
      if (item[variantLabel]) {
        if (variantType) {
          item[variantLabel][variantType] = item[variantLabel][variantType] || {};
          if (variantFlavor) {
            item[variantLabel][variantType][variantFlavor] = data;
          } else {
            item[variantLabel][variantType] = data;
          }

          isFound = true;
        } else {
          item[variantLabel] = data;
          isFound = true;
        }
      }
    });

    return isFound;
  };

  useEffect(() => {
    let modifyVariantProducts = [];
    serverVariantProducts?.map((item) => {
      const itemKey = Object.keys(item)?.[0];
      const [variantLabel, variantType, variantFlavor] = itemKey.split("|");

      if (!isObjectParentFound(modifyVariantProducts, variantLabel, variantType, variantFlavor, item[itemKey])) {
        if (variantFlavor) {
          modifyVariantProducts.push({
            [variantLabel]: {
              [variantType]: {
                [variantFlavor]: item[itemKey],
              },
            },
          });
        } else {
          modifyVariantProducts.push({
            [variantLabel]: variantType
              ? {
                  [variantType]: item[itemKey],
                }
              : item[itemKey],
          });
        }
      }
    });
    dispatch(setClientVariants(modifyVariantProducts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return serverVariantProducts?.length && !clientVariants?.length ? (
    <div className="h-[250.38px] w-full animate-pulse">
      <div className="h-48 w-full rounded bg-grey-300" />
      <div className="mt-10 h-30 w-full rounded bg-grey-300" />
      <div className="mt-10 h-148 w-full rounded bg-grey-300" />
    </div>
  ) : (
    <VariantsComponents isMobile={isMobile} />
  );
};

ComboComponents.propTypes = {
  serverVariantProducts: PropTypes.array,
  isMobile: PropTypes.bool,
};
export default React.memo(ComboComponents);
